import React, { useState, useEffect, useMemo, useRef } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogTitle,
  DialogContent,
  IconButton
} from '@material-ui/core';
import { DialogWithFocusProvider } from 'components/layout';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ErrorBoundary from 'components/hoc/ErrorBoundary';
import ClearIcon from '@material-ui/icons/Clear';
import {
  setPostAcqUWMsrHomesMarkerState,
  setPostAcqUWMsrHomesGridCurrentViewData,
  setPostAcqUWMsrHomesMapState,
  setPostAcqUWMsrHomes,
  setPropertyUWMsrHomesMarkerState,
  setPropertyUWMsrHomesGridCurrentViewData,
  setPropertyUWMsrHomesMapState,
  setSelectedOpportunityMsrHomes
} from 'store/actions';
import {
  Column,
  Grouping,
  Paging,
  SearchPanel,
  Sorting,
  Selection,
  HeaderFilter,
  ColumnFixing,
  StateStoring,
  Summary,
  TotalItem,
  Export
} from 'devextreme-react/data-grid';
import Query from "devextreme/data/query";
import { dataGridExtension } from 'components/devExtreme';
import DataGrid from 'components/devExtreme/DataGrid';
import CustomColumnChooser from 'components/customColumnChooser';
import columnSchema from 'components/underwrite/shared/msrHomesColumnSchema';
import { parseISO, format } from 'date-fns';
import debounce from 'lodash/debounce';
import { MsrHomesMap } from 'components/underwrite/shared';
import { LoadingOverlayWrapper } from 'components/layout';
import { Constants } from 'utilities/constants';

const MsrHomesModal = ({
  module,
  isOpen,
  classes,
  setIsOpen,
  selectedOpportunityMsrHomes,
  selectedMarker,
  propertyUWMsrHomesGridCurrentViewData,
  propertyUWMsrHomesMarkerState,
  setMsrHomesMarkerState,
  setMsrHomesGridCurrentViewData,
  setMsrHomesMapState,
  zoom,
  center,
  gestureHandling,
  mapViewType,
  selectedOpportunity,
  isFetchingMsrHomes,
  setMsrHomes,
  hideUWGridColumnChooser,
}) => {
  const [gridInitialized, setGridInitialized] = useState(false);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);
  const msrHomesGridRef = useRef(null);

  const onSetGridRef = g => {
    msrHomesGridRef.current = g;
    setGridInitialized(true);
  }

  useEffect(() => {
    if (isOpen && hideUWGridColumnChooser) {
      hideUWGridColumnChooser();
    }
  }, [isOpen]);

  useEffect(() => {
    if (msrHomesGridRef.current && selectedOpportunityMsrHomes.length) {
      setViewDataFromGridFilter();
    }
  }, [selectedOpportunityMsrHomes, gridInitialized]);

  useEffect(() => {
    if (msrHomesGridRef.current && selectedMarker) {
      const gridRef = msrHomesGridRef.current.instance;
      const dataIndex = propertyUWMsrHomesGridCurrentViewData.indexOf(selectedMarker.asgCompPropertyId);
      if (!gridRef.isRowFocused(selectedMarker.asgCompPropertyId) && dataIndex !== -1 ) {
        gridRef.option("focusedRowKey", selectedMarker.asgCompPropertyId);
      } else if (dataIndex === -1) {
        gridRef.option("focusedRowIndex", -1);
      }
    } else if (msrHomesGridRef.current) {
      const gridRef = msrHomesGridRef.current.instance;
      gridRef.option("focusedRowIndex", -1);
    }
  }, [selectedMarker, gridInitialized]);

  const onZoomChanged = debounce((map) => {
    setMsrHomesMapState({ zoom: map.getZoom() });        // we store the zoom so that when we navigate away and navigate back, that we can set it back to the original zoom
  }, 900);
  const onCenterChanged = debounce((map) => {
    const getCenterFunc = map.getCenter();
    setMsrHomesMapState({ center: { lat: getCenterFunc.lat(), lng: getCenterFunc.lng() } });
  }, 900);

  const setSelectedMarker = marker => {
    setMsrHomesMarkerState({ selectedMarker: marker });
  };

  const msrHomesMap = useMemo(() =>
    // <MapWrapper
    //   zoom={zoom}
    //   center={center}
    //   mapViewType={mapViewType}
    //   onZoomChanged={onZoomChanged}
    //   onCenterChanged={onCenterChanged}
    //   gestureHandling={gestureHandling}
    // >
    //   <PropertyUWMsrHomesMap setSelectedMarker={setSelectedMarker} />
    // </MapWrapper>
    <MsrHomesMap
      module={module}
      setSelectedMarker={setSelectedMarker}
      selectedMarkerLabel='rentValue'
      zoom={zoom}
      center={center}
      mapViewType={mapViewType}
      onZoomChanged={onZoomChanged}
      onCenterChanged={onCenterChanged}
      gestureHandling={gestureHandling}/>
    , [zoom, center, mapViewType, gestureHandling]);
  let dataSource = useMemo(() => {
    if (selectedOpportunityMsrHomes) {
      const withParsedDates = selectedOpportunityMsrHomes.map(msrHome => {
        const parsedDates = {};
        if (msrHome.acquisitionDate) parsedDates.acquisitionDate = format(parseISO(msrHome.acquisitionDate), 'MM/dd/yyyy');
        if (msrHome.leaseDate) parsedDates.leaseDate = format(parseISO(msrHome.leaseDate), 'MM/dd/yyyy');
        if (msrHome.originalAcquisitionDate) parsedDates.originalAcquisitionDate = format(parseISO(msrHome.originalAcquisitionDate), 'MM/dd/yyyy');

        return {
          ...msrHome,
          ...parsedDates,
        }
      })
      return withParsedDates;
    }
    return null;
  }, [selectedOpportunityMsrHomes]);

  const setViewDataFromGridFilter = () => {
    const gridRef = msrHomesGridRef.current.instance;
    const filterValue = gridRef.option('filterValue');
    const filterPanelEnabled = gridRef.option('filterPanel').filterEnabled;
    let visibleRowHomeIds;
    if (filterValue != null && filterPanelEnabled) {
      const combinedFilter = gridRef.getCombinedFilter();
      visibleRowHomeIds = Query(selectedOpportunityMsrHomes)
        .filter(combinedFilter)
        .toArray()
        .map(row => row.asgCompPropertyId);
    } else {
      visibleRowHomeIds = selectedOpportunityMsrHomes.map(row => row.asgCompPropertyId);
    }
    setMsrHomesGridCurrentViewData(visibleRowHomeIds);
  }

  const handleClose = () => {
    setMsrHomesMarkerState({ selectedMarker: null });
    setMsrHomes([]);
    setIsOpen(false);
  }
  const getCellRender = (cell) => {
    if (selectedOpportunityMsrHomes.length === 0 || cell.value === null || cell.value === undefined) {
      if (cell.column.dataField === 'currentRentPerSqFt') {
        let rent = cell.data.rentValue;
        let sqft = cell.data.squareFeet;
        if (rent && sqft) {
          return <div>{`$${(rent / sqft).toFixed(2)}`}</div>
        }
      }
      return null;
    }
  }
  let columns = useMemo(() => {
    return columnSchema.map(({ field, displayName, width, type, format, visible, sortIndex, sortOrder, headerFilter }) => {
      // let fixed = ['showCheckbox', 'id'].includes(field);
      const rest = {};
      if (['currentRentPerSqFt'].includes(field)) {
        rest.cellRender = getCellRender;
      }
      return (
        <Column
          key={field}
          dataField={field}
          caption={displayName}
          // fixed={fixed}
          width={width}
          dataType={type}
          format={format}
          allowSorting={true}
          allowGrouping={true}
          visible={visible !== false}
          sortIndex={sortIndex}
          sortOrder={sortOrder}
          {...rest}
        >
          <HeaderFilter dataSource={(options) => dataGridExtension.headerFilterSort(options, dataSource, field, type, headerFilter)} />
        </Column>
      )
    })
  }, [dataSource]);

  const errorBoundaryFallback = (errorInfo) => {
    return (
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography variant='subtitle2'>Something went wrong.</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant='body2'>
            {errorInfo?.error && errorInfo.error.toString()}
          </Typography>
        </AccordionDetails>
      </Accordion>
    )
  }

  const onFocusedRowChanged = (e) => {
    if (e?.rowElement?.[0]?.attributes?.['aria-rowindex']?.value != null) {// crazy, but e.row doesnt match e.rowElement
      let visibleRows = e.component.getVisibleRows();
      let rowData = visibleRows[e.rowIndex].data;
      if (rowData.asgCompPropertyId !== selectedMarker?.asgCompPropertyId) {
        setMsrHomesMarkerState({ isFetchingImages: false, selectedMarker: rowData });
      }
    }
  }

  const onToolbarPreparing = e => {
    // this adds a column chooser icon so we can customize its behaviour
    e.toolbarOptions.items.unshift({
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'columnchooser',
        elementAttr: {
          id: 'propertyUWMsrHomesGridColumnChooserBtn'
        },
        onClick: () => setIsColumnChooserVisible(true)
      }
    });
  }

  const onExporting = (e) => {
    const timestamp = format(new Date(), '_yyyy-MM-dd_HHmmss');
    const fileName = `MSRComps_${selectedOpportunity.street}${timestamp}`;
    dataGridExtension.exportToExcel(msrHomesGridRef, fileName);
  }

  return (

    <DialogWithFocusProvider
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
    >
      <DialogTitle disableTypography={true} className={classes.dialogTitleRoot}>
        <Typography variant={'h6'}>
          <span>MSR Homes</span>
        </Typography>
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={handleClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent className={classes.dialogContent}>
        <LoadingOverlayWrapper
          className={classes.overlay}
          active={isFetchingMsrHomes}
          message={'Fetching MSR Homes'}
        >
          <Paper id='msrHomesMapSection' className={classes.paperSection} style={{ flexGrow: 1, height: 350 }} elevation={0}>
            <ErrorBoundary fallbackRender={errorBoundaryFallback}>
              {msrHomesMap}
            </ErrorBoundary>
          </Paper>
          <DataGrid
            id='msrHomesGrid'
            ref={onSetGridRef}
            dataSource={selectedOpportunityMsrHomes}
            keyExpr='asgCompPropertyId'
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnResizingMode='widget'
            showRowLines={true}
            showColumnLines={true}
            height={350}
            width={'100%'}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            focusedRowEnabled={true}
            onFocusedRowChanged={onFocusedRowChanged}
            onExporting={onExporting}
            onToolbarPreparing={onToolbarPreparing}
            enableTooltips
            tooltips={columnSchema}
          >
            <Selection mode={'single'} />
            <ColumnFixing enable={true} />
            {/* <FilterPanel visible={true} /> */}
            {/* <FilterBuilder fields={dataGridExtension.sortFields(columnSchema)} /> */}
            {/* <FilterBuilderPopup /> */}
            <Export enabled={true} texts={{ exportAll: 'Export visible rows to Excel' }}/>
            <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
            <Sorting mode={'multiple'} />
            <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
            <Grouping />
            <Paging enabled={false} />
            <StateStoring
              enabled={false}
              type={'custom'}
              storageKey={'storage'}
              customSave={state => console.log(state)}
            />
            {columns}
            <Summary>
              <TotalItem
                column={'Status'}
                summaryType={'count'} />
            </Summary>
          </DataGrid>
          <CustomColumnChooser
            container="#msrHomesGrid"
            visible={isColumnChooserVisible}
            onHiding={() => setIsColumnChooserVisible(false)}
            columns={columns}
            gridRef={msrHomesGridRef}
          />
        </LoadingOverlayWrapper>
      </DialogContent>
    </DialogWithFocusProvider>
  );
};

const styles = theme => ({
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
  filterField: {
    marginTop: 16
  },
  dialogTitleRoot: {
    paddingBottom: 0
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  overlay: {
    height: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  dialogContent: {
    overflow: 'hidden'
  }
});

const mapState = (state, ownProps) => {
  const moduleState = ownProps.module === Constants.Module.POSTACQ ? state.postAcqUWState : state.propertyUWState;
  return {
    selectedOpportunity: ownProps.module === Constants.Module.POSTACQ ? moduleState.postAcqUWClientTask : moduleState.propertyUWClientTxnOpp,
    selectedOpportunityMsrHomes: moduleState.selectedOpportunityMsrHomes,
    propertyUWMsrHomesMarkerState: moduleState.propertyUWMsrHomesMarkerState,
    selectedMarker: moduleState.propertyUWMsrHomesMarkerState.selectedMarker,
    propertyUWMsrHomesGridCurrentViewData: moduleState.propertyUWMsrHomesGridCurrentViewData,
    zoom: moduleState.propertyUWMsrHomesMapState.zoom,
    center: moduleState.propertyUWMsrHomesMapState.center,
    gestureHandling: moduleState.propertyUWMsrHomesMapState.gestureHandling,
    mapViewType: moduleState.propertyUWMsrHomesMapState.mapViewType,
    isFetchingMsrHomes: moduleState.isFetchingMsrHomes
  }
};

const mapDispatch = (dispatch, ownProps) => {
  const actions = ownProps.module === Constants.Module.POSTACQ
    ? {
      setMsrHomesMarkerState: setPostAcqUWMsrHomesMarkerState,
      setMsrHomesGridCurrentViewData: setPostAcqUWMsrHomesGridCurrentViewData,
      setMsrHomesMapState: setPostAcqUWMsrHomesMapState,
      setMsrHomes: setPostAcqUWMsrHomes
    }
    : {
      setMsrHomesMarkerState: setPropertyUWMsrHomesMarkerState,
      setMsrHomesGridCurrentViewData: setPropertyUWMsrHomesGridCurrentViewData,
      setMsrHomesMapState: setPropertyUWMsrHomesMapState,
      setMsrHomes: setSelectedOpportunityMsrHomes
    };
  return {
    setMsrHomesMarkerState: state => dispatch(actions.setMsrHomesMarkerState(state)),
    setMsrHomesGridCurrentViewData: data => dispatch(actions.setMsrHomesGridCurrentViewData(data)),
    setMsrHomesMapState: state => dispatch(actions.setMsrHomesMapState(state)),
    setMsrHomes: homes => dispatch(actions.setMsrHomes(homes))
  }
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(MsrHomesModal);
